// InformedDecisionsStudyWorkAbroad.js
import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Brain, Target, CheckCircle, AlertCircle, Scale, TrendingUp, Shield } from 'lucide-react';

const InformedDecisionsStudyWorkAbroad = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Image */}
      <section className="relative py-20 bg-gradient-to-br from-primary to-secondary">
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-30"
            style={{ 
              backgroundImage: `url(${require('../../assets/blog-3.jpg')})`,
            }}
          />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-white"
          >
            <Link 
              to="/blog" 
              className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition"
            >
              <ArrowLeft size={20} />
              Back to Blog
            </Link>
            
            <div className="flex items-center gap-4 text-sm font-light mb-6">
              <div className="flex items-center gap-2">
                <Calendar size={18} />
                <span>July 24, 2024</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock size={18} />
                <span>7 min read</span>
              </div>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-semibold mb-6 leading-tight">
              Should You Go At All? A Sober Look Before You Commit
            </h1>
            
            <p className="text-xl font-light leading-relaxed text-white/90">
              Before the applications and the visa forms, there is a plainer question worth sitting with. We look at the real cost, the honest timeline, the reasons people give for going, and the points at which it is still sensible to change your mind.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="prose prose-lg max-w-none"
          >
            {/* Introduction */}
            <div className="mb-12">
              <h2 className="text-3xl font-semibold text-primary mb-4">The Question Nobody Asks Out Loud</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Almost everything written about moving abroad assumes you have already decided to. The advice starts at the application form and works forwards. That is useful once you are committed, and no help at all in the months before, when the honest position is that you are not sure.
              </p>
              <p className="text-gray-600 leading-relaxed">
                This piece is for that earlier stage. It sets out what a move actually costs, how long it really takes, which motivations tend to hold up and which do not, and where the sensible exit points are. Deciding not to go, or to go two years later, is a legitimate outcome, and reaching it deliberately is better than drifting into a decision either way.
              </p>
            </div>

            {/* Money first */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gray-50 rounded-lg flex items-center justify-center">
                  <Scale className="text-primary" size={24} />
                </div>
                <h2 className="text-3xl font-semibold text-primary">Start With What It Costs</h2>
              </div>
              
              <h3 className="text-xl font-semibold text-primary mb-4">The Four Layers of Cost</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Money is the first thing to model because it is the constraint least responsive to determination. Most people count only the first of these four:
              </p>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">What you will be invoiced for:</strong> fees, permit charges, health surcharges, flights and rent
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">What nobody invoices you for:</strong> deposits, translations, certified copies, insurance, and the flight home when something happens
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">What you stop earning:</strong> the salary and progression you set aside while you are studying or re-establishing yourself
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">What it is meant to return:</strong> the earnings, qualification or status that justify the other three, and how many years it takes to arrive
                  </div>
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-primary mb-4">Where Budgets Break</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Plans rarely fail on the big number. They fail on the assumptions underneath it:
              </p>
              
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-primary mb-2">Currency</h4>
                  <p className="text-sm text-gray-600">You earn or save in one currency and spend in another, and the gap moves against you as easily as for you</p>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-primary mb-2">Expected income</h4>
                  <p className="text-sm text-gray-600">Part-time hours you counted on may be capped by your permit, or simply not available where you land</p>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-primary mb-2">Funding not yet granted</h4>
                  <p className="text-sm text-gray-600">A scholarship you have applied for is not a scholarship you have, and the two should be modelled separately</p>
                </div>
              </div>
            </div>

            {/* Motivations */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gray-50 rounded-lg flex items-center justify-center">
                  <Brain className="text-primary" size={24} />
                </div>
                <h2 className="text-3xl font-semibold text-primary">Then Ask Why, Properly</h2>
              </div>
              
              <h3 className="text-xl font-semibold text-primary mb-4">Reasons That Tend to Survive Contact</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Motivations that point at something specific hold up when the first difficult month arrives. Motivations that point away from something usually do not. Test yours against these:
              </p>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">A named outcome:</strong> a qualification, a licence, a market, a role that exists there and not here
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Something you cannot get locally:</strong> a supervisor, a facility, a body of work you want to be near
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">A deliberate change of life:</strong> chosen with clear eyes, rather than as an escape from a situation that will still be there when you return
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">A place in a longer plan:</strong> you can say what the five years after the move look like, even roughly
                  </div>
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-primary mb-4">Readiness Is Not the Same as Willingness</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Wanting to go and being ready to go are different questions, and it is worth answering them separately:
              </p>
              
              <div className="bg-gray-50 p-6 rounded-lg mb-8">
                <h4 className="font-semibold text-primary mb-4">Two Columns Worth Filling In:</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h5 className="font-medium text-gray-800 mb-2">How you cope</h5>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Being unknown for a while</li>
                      <li>• Administrative frustration</li>
                      <li>• Making decisions without advice nearby</li>
                      <li>• Long stretches of unfamiliarity</li>
                    </ul>
                  </div>
                  <div>
                    <h5 className="font-medium text-gray-800 mb-2">What you have in place</h5>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Language at a working level</li>
                      <li>• Funds that survive a bad quarter</li>
                      <li>• Someone reachable in an emergency</li>
                      <li>• Health needs that travel with you</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Timeline */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gray-50 rounded-lg flex items-center justify-center">
                  <TrendingUp className="text-primary" size={24} />
                </div>
                <h2 className="text-3xl font-semibold text-primary">How Long It Actually Takes</h2>
              </div>
              
              <h3 className="text-xl font-semibold text-primary mb-4">Roughly Two Years, End to End</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                People tend to imagine a few months. In practice the stages stack up, and the later ones cannot start until the earlier ones finish:
              </p>
              
              <div className="bg-gray-50 p-6 rounded-lg mb-8">
                <div className="space-y-4">
                  <div className="pb-3 border-b border-gray-200">
                    <span className="font-medium text-gray-800">Working out whether to go</span>
                    <p className="text-sm text-gray-600 mt-1">Three to six months of reading, costing and talking to people who have done it</p>
                  </div>
                  <div className="pb-3 border-b border-gray-200">
                    <span className="font-medium text-gray-800">Becoming eligible and applying</span>
                    <p className="text-sm text-gray-600 mt-1">Six months to a year for tests, documents, applications and the waiting between them</p>
                  </div>
                  <div className="pb-3 border-b border-gray-200">
                    <span className="font-medium text-gray-800">Permits and packing</span>
                    <p className="text-sm text-gray-600 mt-1">One to three months, largely outside your control and easily longer in busy seasons</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-800">Feeling settled</span>
                    <p className="text-sm text-gray-600 mt-1">Another three to six months after arrival before the place stops feeling temporary</p>
                  </div>
                </div>
              </div>

              <h3 className="text-xl font-semibold text-primary mb-4">Dates Set by Other People</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Some deadlines are yours to manage. These are not, and they should anchor the plan:
              </p>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Closing dates:</strong> for admissions rounds, funding calls and job postings, which rarely move
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Processing times:</strong> published as averages, worth planning against the slower end
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">The first non-refundable payment:</strong> the point at which changing your mind starts costing money
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Housing seasons:</strong> in many cities, everything reasonable is gone by a predictable month
                  </div>
                </li>
              </ul>
            </div>

            {/* Where it goes wrong */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gray-50 rounded-lg flex items-center justify-center">
                  <Shield className="text-primary" size={24} />
                </div>
                <h2 className="text-3xl font-semibold text-primary">The Four Ways Plans Come Apart</h2>
              </div>
              
              <h3 className="text-xl font-semibold text-primary mb-4">Failures We See Repeatedly</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                The same handful of problems account for most of the cases that reach us late, after something has already gone wrong:
              </p>
              
              <div className="space-y-6 mb-8">
                <div className="border-l-4 border-primary pl-6">
                  <h4 className="font-semibold text-gray-800 mb-2">Aiming only high</h4>
                  <p className="text-gray-600">Every application sent to places that were always a stretch, leaving nothing to fall back on when the answers come in.</p>
                </div>
                <div className="border-l-4 border-primary pl-6">
                  <h4 className="font-semibold text-gray-800 mb-2">Reading the wrong sources</h4>
                  <p className="text-gray-600">Rules taken from a forum post or an agent with something to sell, rather than from the authority that publishes them and updates them.</p>
                </div>
                <div className="border-l-4 border-primary pl-6">
                  <h4 className="font-semibold text-gray-800 mb-2">Starting late</h4>
                  <p className="text-gray-600">Beginning at a point that would have worked if nothing went wrong, which is not how any of these processes behave.</p>
                </div>
                <div className="border-l-4 border-primary pl-6">
                  <h4 className="font-semibold text-gray-800 mb-2">Small paperwork errors</h4>
                  <p className="text-gray-600">A missing certification or an inconsistent name across documents, each trivial in itself and each capable of costing an entire cycle.</p>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg mb-8">
                <div className="flex items-start gap-3">
                  <AlertCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">What advice can and cannot do:</h4>
                    <p className="text-gray-600">Nobody can promise you an outcome, and you should be wary of anyone who does. What an experienced adviser can do is tell you which requirements are current, where your plan is weakest, and when a route is not worth the money. Those are the decisions that are cheap to change early and expensive to change later.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Deciding */}
            <div className="mb-12">
              <h2 className="text-3xl font-semibold text-primary mb-6">Deciding, and Leaving Yourself a Way Back</h2>
              
              <h3 className="text-xl font-semibold text-primary mb-4">Signs You Are Ready, and Signs You Are Not</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Set your own thresholds before you are emotionally invested, and write them down while you can still be objective:
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h4 className="font-semibold text-primary mb-3">Proceed</h4>
                  <ul className="text-sm text-gray-600 space-y-2">
                    <li>• You can name what the move is for</li>
                    <li>• The money exists, not just the plan for it</li>
                    <li>• You meet the criteria as written today</li>
                    <li>• Someone would notice if things went badly</li>
                    <li>• Your timeline survives a delay</li>
                  </ul>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h4 className="font-semibold text-primary mb-3">Wait, or reconsider</h4>
                  <ul className="text-sm text-gray-600 space-y-2">
                    <li>• The reason changes each time you explain it</li>
                    <li>• The budget only works if nothing goes wrong</li>
                    <li>• Eligibility depends on a rule you hope will change</li>
                    <li>• You are going mainly to get away</li>
                    <li>• Every date is already the latest possible one</li>
                  </ul>
                </div>
              </div>

              <h3 className="text-xl font-semibold text-primary mb-4">Keep the Alternatives Open</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Committing fully to one path is not the same as having no others. Hold these lightly in reserve:
              </p>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">A second destination</strong> whose requirements you already meet
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">The same plan, one cycle later,</strong> which is often the strongest version of it
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">A defined point to come home,</strong> agreed with yourself in advance rather than in a bad week
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">A route to the same goal from here,</strong> if one exists, so the comparison is honest
                  </div>
                </li>
              </ul>
            </div>

            {/* Conclusion */}
            <div className="mb-12">
              <h2 className="text-3xl font-semibold text-primary mb-6">Where That Leaves You</h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                A decision made carefully at this stage does not remove the difficulty of moving abroad. It changes the nature of it. The difficulties become the ones you anticipated and budgeted for, rather than the ones that arrive unannounced in month three and force a choice you never wanted to make.
              </p>
              <p className="text-gray-600 leading-relaxed mb-8">
                So take the time. Cost it properly, write down why, and be specific about what would have to be true for you to stop. If the answer at the end is still yes, you will start from a much stronger position than most people do.
              </p>
              
              <div className="bg-gradient-to-r from-primary to-secondary p-8 rounded-lg text-white">
                <h3 className="text-2xl font-semibold mb-4">Still Weighing It Up?</h3>
                <p className="mb-6 text-white/90">
                  Ivipass can talk through your circumstances without pushing you towards a decision, and set out what each option would realistically involve.
                </p>
                <button className="bg-white text-primary px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition">
                  Talk It Through With Us
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default InformedDecisionsStudyWorkAbroad;
